import { Controller } from "@hotwired/stimulus";
import { smartMatch } from "../../utils/smart_search";

export default class extends Controller {
  static targets = [
    "scopeSelector",
    "selectionSection",
    "studentsSelection",
    "classesSelection",
    "schoolSelection",
    "studentSearch",
    "studentList",
    "selectedStudentsCount",
    "studentCount",
    "selectedClassInfo",
    "classCount",
    "watermarkUpload",
    "primaryColorUpload",
    "secondaryColorUpload",
    "selectionSummary",
    "scopeText",
    "targetText",
    "estimatedCount",
    "submitButton",
  ];

  static values = {
    translations: Object,
  };

  connect() {
    console.log("Generate card form controller connected");
    this.updateFormState();
  }

  t(key, options = {}) {
    let text = this.translationsValue[key] || key;
    Object.keys(options).forEach((k) => {
      text = text.replace(`%{${k}}`, options[k]);
    });
    return text;
  }

  updateScope(event) {
    const scope = event.target.value;
    this.hideAllSelectionSections();
    this.updateScopeDisplay(scope);

    switch (scope) {
      case "students":
        this.showStudentsSelection();
        break;
      case "classes":
        this.showClassesSelection();
        break;
      case "school":
        this.showSchoolSelection();
        break;
    }

    this.updateFormState();
  }

  hideAllSelectionSections() {
    if (this.hasStudentsSelectionTarget) {
      this.studentsSelectionTarget.classList.add("hidden");
    }
    if (this.hasClassesSelectionTarget) {
      this.classesSelectionTarget.classList.add("hidden");
    }
    if (this.hasSchoolSelectionTarget) {
      this.schoolSelectionTarget.classList.add("hidden");
    }
  }

  showStudentsSelection() {
    if (this.hasStudentsSelectionTarget) {
      this.studentsSelectionTarget.classList.remove("hidden");
      this.resetStudentSelection();
    }
  }

  showClassesSelection() {
    if (this.hasClassesSelectionTarget) {
      this.classesSelectionTarget.classList.remove("hidden");
      this.resetClassSelection();
    }
  }

  showSchoolSelection() {
    if (this.hasSchoolSelectionTarget) {
      this.schoolSelectionTarget.classList.remove("hidden");
      this.updateTargetDisplay(this.t("scope_school"), this.t("all_students"));
    }
  }

  resetStudentSelection() {
    // Uncheck all student checkboxes
    const checkboxes = this.element.querySelectorAll(
      'input[name="generate_card[target_ids][]"]',
    );
    checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.updateStudentSelection();
  }

  resetClassSelection() {
    // Uncheck all class radio buttons
    const radios = this.element.querySelectorAll(
      'input[name="generate_card[target_ids]"]',
    );
    radios.forEach((radio) => {
      radio.checked = false;
    });
    this.updateClassSelection();
  }

  updateStudentSelection() {
    const checkedBoxes = this.element.querySelectorAll(
      'input[name="generate_card[target_ids][]"]:checked',
    );
    const count = checkedBoxes.length;

    if (this.hasStudentCountTarget) {
      this.studentCountTarget.textContent = count;
    }

    if (count > 0) {
      const names = Array.from(checkedBoxes)
        .slice(0, 3)
        .map((cb) => cb.getAttribute("data-student-name"));
      const displayText =
        count <= 3
          ? names.join(", ")
          : `${names.join(", ")} ${this.t("and_others", { count: count - 3 })}`;
      this.updateTargetDisplay(
        this.t("scope_student"),
        displayText,
        this.t("students_count", { count }),
      );
    } else {
      this.updateTargetDisplay(
        this.t("no_student_selected"),
        this.t("students_title"),
        this.t("students_count", { count: 0 }),
      );
    }

    this.updateFormState();
  }

  updateClassSelection(event) {
    // Récupérer toutes les classes sélectionnées
    const selectedClasses = this.element.querySelectorAll(
      'input[name="generate_card[target_ids][]"]:checked',
    );
    const classNames = [];
    let totalStudents = 0;

    selectedClasses.forEach((checkbox) => {
      const className = checkbox.getAttribute("data-class-name");
      const studentCount = parseInt(
        checkbox.getAttribute("data-student-count"),
      );

      if (className && !isNaN(studentCount)) {
        classNames.push(className);
        totalStudents += studentCount;
      }
    });

    // Mettre à jour l'affichage
    if (this.hasSelectedClassInfoTarget) {
      if (classNames.length === 0) {
        this.selectedClassInfoTarget.textContent = this.t("no_class_selected");
      } else if (classNames.length === 1) {
        this.selectedClassInfoTarget.textContent = `${classNames[0]} - ${this.t("students_count", { count: totalStudents })}`;
      } else {
        this.selectedClassInfoTarget.textContent = `${classNames.length} ${this.t("classes_selected")} - ${this.t("students_count", { count: totalStudents })}`;
      }
    }

    // Mettre à jour le compteur de classes
    if (this.hasClassCountTarget) {
      this.classCountTarget.textContent = classNames.length;
    }

    // Mettre à jour le récapitulatif
    const displayText =
      classNames.length > 1
        ? classNames.join(", ")
        : classNames[0] || this.t("none_class");
    this.updateTargetDisplay(
      this.t("classes_title"),
      displayText,
      this.t("students_count", { count: totalStudents }),
    );
    this.updateFormState();
  }

  filterStudents() {
    this.applyStudentFilter();
  }

  setPhotoFilter(event) {
    const button = event.currentTarget;
    this.photoFilter = button.getAttribute("data-filter") || "all";

    button.parentElement
      .querySelectorAll(".ds-segmented__btn")
      .forEach((btn) => {
        btn.setAttribute("aria-pressed", btn === button ? "true" : "false");
      });

    this.applyStudentFilter();
  }

  applyStudentFilter() {
    const searchTerm = this.hasStudentSearchTarget
      ? this.studentSearchTarget.value
      : "";
    const photoFilter = this.photoFilter || "all";

    this.element
      .querySelectorAll('input[name="generate_card[target_ids][]"]')
      .forEach((checkbox) => {
        const label = checkbox.closest("label");
        if (!label) return;

        const studentName = checkbox.getAttribute("data-student-name") || "";
        const studentClass = checkbox.getAttribute("data-student-class") || "";
        const hasPhoto = checkbox.getAttribute("data-has-photo") === "true";

        const matchesSearch = smartMatch([studentName, studentClass], searchTerm);
        const matchesPhoto =
          photoFilter === "all" ||
          (photoFilter === "with" && hasPhoto) ||
          (photoFilter === "without" && !hasPhoto);

        label.style.display = matchesSearch && matchesPhoto ? "block" : "none";
      });
  }

  toggleWatermarkUpload(event) {
    const isChecked = event.target.checked;

    if (this.hasWatermarkUploadTarget) {
      if (isChecked) {
        this.watermarkUploadTarget.classList.remove("hidden");
      } else {
        this.watermarkUploadTarget.classList.add("hidden");
      }
    }
  }

  togglePrimaryColor(event) {
    const isChecked = event.target.checked;

    if (this.hasPrimaryColorUploadTarget) {
      if (isChecked) {
        this.primaryColorUploadTarget.classList.remove("hidden");
      } else {
        this.primaryColorUploadTarget.classList.add("hidden");
      }
    }
  }

  toggleSecondaryColor(event) {
    const isChecked = event.target.checked;

    if (this.hasSecondaryColorUploadTarget) {
      if (isChecked) {
        this.secondaryColorUploadTarget.classList.remove("hidden");
      } else {
        this.secondaryColorUploadTarget.classList.add("hidden");
      }
    }
  }

  updateScopeDisplay(scope) {
    const scopeTexts = {
      students: this.t("students_title"),
      classes: this.t("classes_title"),
      school: this.t("scope_school"),
    };

    if (this.hasScopeTextTarget) {
      this.scopeTextTarget.textContent =
        scopeTexts[scope] || this.t("not_selected");
    }
  }

  updateTargetDisplay(label, description, count = "0 cartes") {
    if (this.hasTargetTextTarget) {
      this.targetTextTarget.textContent = description;
    }

    if (this.hasEstimatedCountTarget) {
      this.estimatedCountTarget.textContent = count;
    }
  }

  updateFormState() {
    const scope = this.element.querySelector(
      'input[name="generate_card[generation_scope]"]:checked',
    )?.value;
    let isValid = false;

    if (scope) {
      switch (scope) {
        case "students":
          const selectedStudents = this.element.querySelectorAll(
            'input[name="generate_card[target_ids][]"]:checked',
          );
          isValid = selectedStudents.length > 0;
          break;
        case "classes":
          const selectedClasses = this.element.querySelectorAll(
            'input[name="generate_card[target_ids][]"]:checked',
          );
          isValid = selectedClasses.length > 0;
          break;
        case "school":
          isValid = true;
          break;
      }
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !isValid;

      if (isValid) {
        this.submitButtonTarget.classList.remove(
          "opacity-50",
          "cursor-not-allowed",
        );
        this.submitButtonTarget.classList.add("hover:bg-cta-darker");
      } else {
        this.submitButtonTarget.classList.add(
          "opacity-50",
          "cursor-not-allowed",
        );
        this.submitButtonTarget.classList.remove("hover:bg-cta-darker");
      }
    }
  }
}
